import React, { useEffect, ReactElement, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, Image, Button, Stack } from 'react-bootstrap';
import { map, isUndefined, isNull } from 'lodash';
import { useTranslation } from 'react-i18next';

import { BackButton, ClaimBadge, Loading, LoadingError, NoData } from '@components/Base';
import { PaymentMethods } from '@models/PaymentInfo';
import { ClaimableTypes } from '@models/Claim';
import { amountToUsd } from '@utils/currency';
import { formatDateTime } from '@utils/datetime';
import { getAttachmentType, isAttachmentImage, isAttachmentPdf } from '@utils/attachment';

import { selectCancerClaim } from './selector';
import { cancerClaimFetch } from './action';
import { useEditable } from '../hook';
import { ClaimEditModal } from '../../ClaimEditModal';

interface RowItemProps {
  label: string;
  value?: string | number | ReactElement | null;
  list?: string[];
  renderItem?: (item: string) => void;
}

const RowItem = ({ label, value, list, renderItem }: RowItemProps) => {
  return (
    <Row className="mb-2">
      <Col xs={4} sm={4} md={3}>
        {label}
      </Col>
      <Col xs="auto">:</Col>
      <Col>
        <>
          {value && value}
          {list && renderItem && map(list, renderItem)}
        </>
      </Col>
    </Row>
  );
};

interface ClaimAttachmentItemProps {
  name: string;
  url: string;
}

const ClaimAttachmentItem = ({ name, url }: ClaimAttachmentItemProps) => {
  const { t } = useTranslation();
  const attachmentType = getAttachmentType(url);

  return (
    <>
      {isAttachmentImage(attachmentType) && (
        <Image src={url} thumbnail className="mb-3" data-testid={`${name}-image`} />
      )}

      {isAttachmentPdf(attachmentType) && (
        <Link to={url} className="d-block mb-3" target="_blank" rel="noreferrer" data-testid={`${name}-pdf`}>
          {t('common:clickToViewDetails')}
        </Link>
      )}
    </>
  );
};

const CancerClaimDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { claimableType, claimableId } = useParams();
  const { cancerClaim } = useSelector(selectCancerClaim);
  const isLoaded = !isUndefined(cancerClaim);
  const isError = isNull(cancerClaim);
  const hasCancerClaim = isLoaded && !isNull(cancerClaim);
  const shouldEdit = useEditable(cancerClaim);
  const isPaymentByBankTransfer = cancerClaim?.paymentInfo?.paymentMethod === PaymentMethods.BankTransfer;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleReload = () => {
    dispatch(cancerClaimFetch({ id: Number(claimableId) }));
  };

  useEffect(() => {
    dispatch(cancerClaimFetch({ id: Number(claimableId) }));
  }, [claimableId]);

  return (
    <>
      <BackButton className="mb-4" />
      {!isLoaded && <Loading />}
      {isError && <LoadingError />}
      {isLoaded && !hasCancerClaim && <NoData />}

      {hasCancerClaim && (
        <Card>
          <Card.Header>
            <Stack direction="horizontal" gap={2} className="align-items-center">
              <h5 className="mb-0 me-auto">{t('common:details')}</h5>
              {shouldEdit && (
                <Button size="sm" onClick={() => setIsModalOpen(true)}>
                  {t('common:edit')}
                </Button>
              )}
            </Stack>
          </Card.Header>
          <Card.Body>
            <RowItem label={t('common:status')} value={<ClaimBadge status={cancerClaim.status} />} />
            <RowItem label={t('common:claimNumber')} value={cancerClaim.claimNumber} />
            <RowItem
              label={t('cancerInsuranceClaimForm.accidentHappenedAt.label')}
              value={formatDateTime(cancerClaim.accidentHappenedAt)}
            />
            <RowItem
              label={t('claimForm.supportingDocuments.label')}
              list={cancerClaim.supportingDocuments}
              renderItem={(url) => <ClaimAttachmentItem name="supporting-documents" url={url} />}
            />
            <RowItem
              label={t('common:additionalDocuments')}
              list={cancerClaim.additionalDocuments}
              renderItem={(url) => <ClaimAttachmentItem key={url} name="additional-documents" url={url} />}
            />
            <RowItem
              label={t('claimForm.totalClaimedAmount.label')}
              value={amountToUsd(cancerClaim.totalClaimedAmount)}
            />
            <RowItem label={t('claimForm.approvedAmount.label')} value={amountToUsd(cancerClaim.approvedAmount)} />
            <RowItem label={t('common:note')} value={cancerClaim.note} />
            <hr />
            <RowItem label={t('paymentInfo.paymentMethod')} value={cancerClaim.paymentInfo?.paymentMethod} />
            <RowItem label={t('paymentInfo.beneficiaryName')} value={cancerClaim.paymentInfo?.beneficiaryName} />
            {isPaymentByBankTransfer && (
              <>
                <RowItem label={t('paymentInfo.bankName')} value={cancerClaim.paymentInfo?.bankName} />
                <RowItem label={t('paymentInfo.bankAccount')} value={cancerClaim.paymentInfo?.bankAccount} />
              </>
            )}
          </Card.Body>
        </Card>
      )}

      {cancerClaim && (
        <ClaimEditModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          claimNumber={cancerClaim.claimNumber}
          claimableType={ClaimableTypes.Cancer}
          claimableId={cancerClaim.id}
          currentStatus={cancerClaim.status}
          currentTotalClaimedAmount={cancerClaim.totalClaimedAmount}
          onSuccess={handleReload}
        />
      )}
    </>
  );
};

export { CancerClaimDetails };
